import React, { useState, useCallback } from 'react';
import PropTypes from 'prop-types';
import styled, { css } from 'styled-components';
import { CompressedImage } from 'web-services/Imgix';
import SkeletonLoader from 'web-ui-kit/SkeletonLoader';

function _defineProperty(obj, key, value) {
  if (key in obj) {
    Object.defineProperty(obj, key, {
      value: value,
      enumerable: true,
      configurable: true,
      writable: true
    });
  } else {
    obj[key] = value;
  }

  return obj;
}

function _extends() {
  _extends = Object.assign || function (target) {
    for (var i = 1; i < arguments.length; i++) {
      var source = arguments[i];

      for (var key in source) {
        if (Object.prototype.hasOwnProperty.call(source, key)) {
          target[key] = source[key];
        }
      }
    }

    return target;
  };

  return _extends.apply(this, arguments);
}

function ownKeys(object, enumerableOnly) {
  var keys = Object.keys(object);

  if (Object.getOwnPropertySymbols) {
    var symbols = Object.getOwnPropertySymbols(object);
    if (enumerableOnly) symbols = symbols.filter(function (sym) {
      return Object.getOwnPropertyDescriptor(object, sym).enumerable;
    });
    keys.push.apply(keys, symbols);
  }

  return keys;
}

function _objectSpread2(target) {
  for (var i = 1; i < arguments.length; i++) {
    var source = arguments[i] != null ? arguments[i] : {};

    if (i % 2) {
      ownKeys(Object(source), true).forEach(function (key) {
        _defineProperty(target, key, source[key]);
      });
    } else if (Object.getOwnPropertyDescriptors) {
      Object.defineProperties(target, Object.getOwnPropertyDescriptors(source));
    } else {
      ownKeys(Object(source)).forEach(function (key) {
        Object.defineProperty(target, key, Object.getOwnPropertyDescriptor(source, key));
      });
    }
  }

  return target;
}

function _objectWithoutPropertiesLoose(source, excluded) {
  if (source == null) return {};
  var target = {};
  var sourceKeys = Object.keys(source);
  var key, i;

  for (i = 0; i < sourceKeys.length; i++) {
    key = sourceKeys[i];
    if (excluded.indexOf(key) >= 0) continue;
    target[key] = source[key];
  }

  return target;
}

function _objectWithoutProperties(source, excluded) {
  if (source == null) return {};

  var target = _objectWithoutPropertiesLoose(source, excluded);

  var key, i;

  if (Object.getOwnPropertySymbols) {
    var sourceSymbolKeys = Object.getOwnPropertySymbols(source);

    for (i = 0; i < sourceSymbolKeys.length; i++) {
      key = sourceSymbolKeys[i];
      if (excluded.indexOf(key) >= 0) continue;
      if (!Object.prototype.propertyIsEnumerable.call(source, key)) continue;
      target[key] = source[key];
    }
  }

  return target;
}

function _taggedTemplateLiteral(strings, raw) {
  if (!raw) {
    raw = strings.slice(0);
  }

  return Object.freeze(Object.defineProperties(strings, {
    raw: {
      value: Object.freeze(raw)
    }
  }));
}

function _slicedToArray(arr, i) {
  return _arrayWithHoles(arr) || _iterableToArrayLimit(arr, i) || _unsupportedIterableToArray(arr, i) || _nonIterableRest();
}

function _arrayWithHoles(arr) {
  if (Array.isArray(arr)) return arr;
}

function _iterableToArrayLimit(arr, i) {
  if (typeof Symbol === "undefined" || !(Symbol.iterator in Object(arr))) return;
  var _arr = [];
  var _n = true;
  var _d = false;
  var _e = undefined;

  try {
    for (var _i = arr[Symbol.iterator](), _s; !(_n = (_s = _i.next()).done); _n = true) {
      _arr.push(_s.value);

      if (i && _arr.length === i) break;
    }
  } catch (err) {
    _d = true;
    _e = err;
  } finally {
    try {
      if (!_n && _i["return"] != null) _i["return"]();
    } finally {
      if (_d) throw _e;
    }
  }

  return _arr;
}

function _unsupportedIterableToArray(o, minLen) {
  if (!o) return;
  if (typeof o === "string") return _arrayLikeToArray(o, minLen);
  var n = Object.prototype.toString.call(o).slice(8, -1);
  if (n === "Object" && o.constructor) n = o.constructor.name;
  if (n === "Map" || n === "Set") return Array.from(n);
  if (n === "Arguments" || /^(?:Ui|I)nt(?:8|16|32)(?:Clamped)?Array$/.test(n)) return _arrayLikeToArray(o, minLen);
}

function _arrayLikeToArray(arr, len) {
  if (len == null || len > arr.length) len = arr.length;

  for (var i = 0, arr2 = new Array(len); i < len; i++) {
    arr2[i] = arr[i];
  }

  return arr2;
}

function _nonIterableRest() {
  throw new TypeError("Invalid attempt to destructure non-iterable instance.\nIn order to be iterable, non-array objects must have a [Symbol.iterator]() method.");
}
/**
 * Hook to control when to show the skeleton loader, and when to control showing
 * the actual image
 * @param src The image src
 */


function useSkeletonImageLoader(_ref) {
  var src = _ref.src; // Track the loading state

  var _useState = useState({
    refLoaded: false,
    onLoadComplete: false
  }),
      _useState2 = _slicedToArray(_useState, 2),
      imageLoadedState = _useState2[0],
      setImageLoadedState = _useState2[1]; // Use a callback ref


  var refFunction = useCallback(function (node) {
    if (node) {
      var refLoaded = node.complete && node.naturalHeight !== 0;

      if (refLoaded !== imageLoadedState.refLoaded) {
        setImageLoadedState(_objectSpread2(_objectSpread2({}, imageLoadedState), {}, {
          refLoaded: node.complete && node.naturalHeight !== 0
        }));
      }
    }
  }, [imageLoadedState, setImageLoadedState]); // Callback for onload when supported

  var onLoad = useCallback(function () {
    setImageLoadedState(_objectSpread2(_objectSpread2({}, imageLoadedState), {}, {
      onLoadComplete: true
    }));
  }, [imageLoadedState, setImageLoadedState]);
  var imageLoaded = (imageLoadedState.refLoaded || imageLoadedState.onLoadComplete) && src !== 'skeleton';
  return {
    imageRef: refFunction,
    loading: !imageLoaded,
    onLoad: onLoad
  };
}

var _templateObject, _templateObject2, _templateObject3, _templateObject4, _templateObject5, _templateObject6;

var _excluded = ["imageRef", "onClick", "onLoad", "src"],
    _excluded2 = ["imageRef", "onClick", "onLoad", "src"];
var SKELETON_LOADER_SRC = 'skeleton';
/**
 * Renders an image with a skeleton loader until the image load has completed
 * - does not assume anything about styling, styling must be passed in using the className
 *   prop from parent
 * - passing a src prop of 'skeleton' will always present the skeleton loader
 */

function ImageWithSkeletonLoader(_ref) {
  var _ref$alt = _ref.alt,
      alt = _ref$alt === void 0 ? '' : _ref$alt,
      _ref$bucketName = _ref.bucketName,
      bucketName = _ref$bucketName === void 0 ? '' : _ref$bucketName,
      _ref$className = _ref.className,
      className = _ref$className === void 0 ? '' : _ref$className,
      _ref$onError = _ref.onError,
      onError = _ref$onError === void 0 ? function () {} : _ref$onError,
      _ref$onClick = _ref.onClick,
      onClick = _ref$onClick === void 0 ? function () {} : _ref$onClick,
      _ref$sizes = _ref.sizes,
      sizes = _ref$sizes === void 0 ? '' : _ref$sizes,
      _ref$src = _ref.src,
      src = _ref$src === void 0 ? SKELETON_LOADER_SRC : _ref$src,
      _ref$useImgix = _ref.useImgix,
      useImgix = _ref$useImgix === void 0 ? true : _ref$useImgix,
      _ref$useLoadingHook = _ref.useLoadingHook,
      useLoadingHook = _ref$useLoadingHook === void 0 ? useSkeletonImageLoader : _ref$useLoadingHook,
      _ref$width = _ref.width,
      width = _ref$width === void 0 ? undefined : _ref$width,
      _ref$height = _ref.height,
      height = _ref$height === void 0 ? undefined : _ref$height;

  var _useLoadingHook = useLoadingHook({
    src: src
  }),
      imageRef = _useLoadingHook.imageRef,
      loading = _useLoadingHook.loading,
      onLoad = _useLoadingHook.onLoad;

  var sharedProps = {
    alt: alt,
    className: className,
    imageRef: imageRef,
    loading: loading,
    onClick: onClick,
    onError: onError,
    onLoad: onLoad,
    src: src,
    width: width,
    height: height
  };
  return useImgix ? renderImgixImage(_objectSpread2({
    bucketName: bucketName,
    sizes: sizes
  }, sharedProps)) : renderImg(sharedProps);
}

ImageWithSkeletonLoader.propTypes = {
  /* The image alt text */
  alt: PropTypes.string,

  /* The imgix bucketName if using imgix */
  bucketName: PropTypes.string,

  /* Styled Components classname */
  className: PropTypes.string,

  /* Sizes prop for imgix */
  sizes: PropTypes.string,

  /* The image src */
  src: PropTypes.string,

  /* Determines whether the image should be rendered using imgix */
  useImgix: PropTypes.bool,

  /* Optional hook for controlling loading behavior, defaults to integrated */
  useLoadingHook: PropTypes.func,

  /* Optional static width for image */
  width: PropTypes.number,

  /* Optional static height for image */
  height: PropTypes.number
};
/**
 * Renders an img component with a skeleton loader
 */

var renderImg = function renderImg(props) {
  var imageRef = props.imageRef,
      onClick = props.onClick,
      onLoad = props.onLoad,
      src = props.src,
      rest = _objectWithoutProperties(props, _excluded);

  if (src === SKELETON_LOADER_SRC) {
    return (
      /*#__PURE__*/
      React.createElement(React.Fragment, null, renderSkeletonLoader(rest))
    );
  }

  return (
    /*#__PURE__*/
    React.createElement(Img, _extends({
      onClick: onClick,
      onLoad: onLoad,
      ref: imageRef,
      src: src
    }, rest))
  );
};
/**
 * Renders an imgix compressed image with a skeleton loader
 */


var renderImgixImage = function renderImgixImage(props) {
  var imageRef = props.imageRef,
      onClick = props.onClick,
      onLoad = props.onLoad,
      src = props.src,
      rest = _objectWithoutProperties(props, _excluded2);

  if (src === SKELETON_LOADER_SRC) {
    return (
      /*#__PURE__*/
      React.createElement(React.Fragment, null, renderSkeletonLoader(rest))
    );
  }

  return (
    /*#__PURE__*/
    React.createElement(ImgixImage, _extends({
      onClick: onClick,
      onLoad: onLoad,
      handleRef: imageRef,
      src: src
    }, rest))
  );
};
/**
 * Renders the skeleton loader
 */


var renderSkeletonLoader = function renderSkeletonLoader(props) {
  return (
    /*#__PURE__*/
    React.createElement(Skeleton, props)
  );
};

var Img = styled.img(_templateObject || (_templateObject = _taggedTemplateLiteral(["\n  ", "\n"])), function (_ref2) {
  var loading = _ref2.loading;
  return loading && css(_templateObject2 || (_templateObject2 = _taggedTemplateLiteral(["display: none;"])));
});
var ImgixImage = styled(CompressedImage)(_templateObject3 || (_templateObject3 = _taggedTemplateLiteral(["\n  ", "\n"])), function (_ref3) {
  var loading = _ref3.loading;
  return loading && css(_templateObject4 || (_templateObject4 = _taggedTemplateLiteral(["display: none;"])));
});
var Skeleton = styled(SkeletonLoader)(_templateObject5 || (_templateObject5 = _taggedTemplateLiteral(["\n  ", "\n"])), function (_ref4) {
  var loading = _ref4.loading;
  return !loading && css(_templateObject6 || (_templateObject6 = _taggedTemplateLiteral(["display: none;"])));
});
export default ImageWithSkeletonLoader;
export { SKELETON_LOADER_SRC };